import { useState, useRef, useEffect } from 'react';
import { colors } from '../constants/colors';
import { X } from 'lucide-react';

interface CustomBidModalProps {
  currentPrice: number;
  minimumBid: number;
  userMaxBidAmount?: number | null;
  onPlaceBid: (amount: number, bidType: 'CUSTOM' | 'MAX_BID') => void;
  onClose: () => void;
}

const INCREMENTS = [1, 5, 10, 25];
const MAX_BID_LIMIT = 100000;

export function CustomBidModal({ currentPrice, minimumBid, userMaxBidAmount, onPlaceBid, onClose }: CustomBidModalProps) {
  const [bidMode, setBidMode] = useState<'CUSTOM' | 'MAX_BID'>('MAX_BID');
  const hasActiveMax = bidMode === 'MAX_BID' && userMaxBidAmount != null;
  const initialAmount = hasActiveMax ? String(Math.ceil(userMaxBidAmount)) : String(minimumBid);
  const [amount, setAmount] = useState(initialAmount);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Update amount when switching modes
  useEffect(() => {
    if (bidMode === 'MAX_BID' && userMaxBidAmount != null) {
      setAmount(String(Math.ceil(userMaxBidAmount)));
    } else {
      setAmount(String(minimumBid));
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [bidMode]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const parsed = parseInt(amount, 10);
  const effectiveMin = hasActiveMax ? Math.ceil(userMaxBidAmount) + 1 : minimumBid;
  const isValid = !isNaN(parsed) && parsed >= effectiveMin && parsed <= MAX_BID_LIMIT;

  const handleSubmit = () => {
    if (!isValid) return;
    onPlaceBid(parsed, bidMode);
    onClose();
  };

  const handleIncrement = (inc: number) => {
    const current = isNaN(parsed) ? effectiveMin : parsed;
    setAmount(String(Math.min(current + inc, MAX_BID_LIMIT)));
  };

  const warningText = !isNaN(parsed) && parsed > MAX_BID_LIMIT
    ? `Maximum bid is $${MAX_BID_LIMIT.toLocaleString()}`
    : hasActiveMax
      ? `Must be higher than your current max of $${Math.ceil(userMaxBidAmount).toLocaleString()}`
      : `Minimum bid is $${effectiveMin}`;

  const buttonLabel = bidMode === 'MAX_BID'
    ? (hasActiveMax ? `Raise Max Bid: $${parsed || 0}` : `Place Max Bid: $${parsed || 0}`)
    : `Place Custom Bid: $${parsed || 0}`;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          {/* Toggle */}
          <div style={styles.toggle}>
            <button
              onClick={() => setBidMode('CUSTOM')}
              style={{
                ...styles.toggleBtn,
                background: bidMode === 'CUSTOM' ? colors.brand.blue : 'transparent',
                color: bidMode === 'CUSTOM' ? '#fff' : colors.ink.black,
              }}
            >
              Custom Bid
            </button>
            <button
              onClick={() => setBidMode('MAX_BID')}
              style={{
                ...styles.toggleBtn,
                background: bidMode === 'MAX_BID' ? colors.brand.blue : 'transparent',
                color: bidMode === 'MAX_BID' ? '#fff' : colors.ink.black,
              }}
            >
              Max Bid
            </button>
          </div>
          <div style={styles.currentBadge}>
            Current: ${Math.ceil(currentPrice)}
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={18} />
          </button>
        </div>

        {/* Active max bid indicator */}
        {hasActiveMax && (
          <div style={styles.activeMaxBanner}>
            Your current max: ${Math.ceil(userMaxBidAmount).toLocaleString()}
          </div>
        )}

        {/* Amount Input */}
        <div style={styles.inputRow}>
          <span style={styles.dollar}>$</span>
          <input
            ref={inputRef}
            type="number"
            min={effectiveMin}
            step="1"
            value={amount}
            onChange={e => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            style={styles.input}
            placeholder="0"
          />
        </div>

        {/* Quick Increment Buttons */}
        <div style={styles.incrementRow}>
          {INCREMENTS.map(inc => (
            <button
              key={inc}
              onClick={() => handleIncrement(inc)}
              style={styles.incrementBtn}
            >
              +${inc}
            </button>
          ))}
        </div>

        {/* Validation */}
        {amount && !isNaN(parsed) && !isValid && (
          <p style={styles.warning}>{warningText}</p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!isValid}
          style={{
            ...styles.submitBtn,
            background: isValid ? colors.brand.blue : colors.gray[300],
            cursor: isValid ? 'pointer' : 'not-allowed',
          }}
        >
          {isValid ? buttonLabel : 'Enter a valid amount'}
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    padding: 24,
    width: 360,
    maxWidth: '90vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
  },
  toggle: {
    display: 'flex',
    background: colors.gray[100],
    borderRadius: 12,
    overflow: 'hidden',
  },
  toggleBtn: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  currentBadge: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.gray[600],
    background: colors.gray[100],
    padding: '4px 10px',
    borderRadius: 999,
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[400],
    padding: 4,
    display: 'flex',
  },
  activeMaxBanner: {
    fontSize: 13,
    fontWeight: 600,
    color: '#16A34A',
    background: '#E8F5E9',
    padding: '8px 12px',
    borderRadius: 10,
    marginBottom: 12,
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    background: colors.gray[100],
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 12,
  },
  dollar: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.gray[400],
    marginRight: 4,
  },
  input: {
    flex: 1,
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    background: 'none',
    border: 'none',
    outline: 'none',
    padding: 0,
    fontFamily: 'inherit',
    MozAppearance: 'textfield',
  },
  incrementRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  incrementBtn: {
    flex: 1,
    padding: '8px 0',
    background: colors.gray[100],
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    color: colors.ink.black,
    cursor: 'pointer',
  },
  warning: {
    fontSize: 13,
    color: colors.accent.pink,
    margin: '0 0 12px',
    fontWeight: 500,
  },
  submitBtn: {
    width: '100%',
    padding: '14px 0',
    borderRadius: 12,
    border: 'none',
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
  },
};
